import React, { useState } from 'react';
import { Copy, CheckCircle2, Users, ArrowRightLeft, Mail } from 'lucide-react';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { DjayNarrator } from '../../../mascot';
import type { Classroom, TeacherAccount } from '../../../accounts';
import { MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
interface InviteCoTeacherPanelProps {
  open: boolean;
  onClose: () => void;
  classroom: Classroom;
  activeAccount: TeacherAccount;
  /** All teachers known to the active account, used to render names of existing co-teachers. */
  teacherById: Record<string, TeacherAccount | undefined>;
  /** Owner-only: open the transfer-ownership confirmation. */
  onRequestTransfer?: (newOwnerId: string) => void;
}
/**
 * Owner-facing panel to invite, list, and manage co-teachers for a classroom.
 *
 * Wave 3.2: We reuse the classroom's existing `joinCode`. The receiving teacher
 * enters this code in the "Ik heb een co-docent-code" flow (Wave 3.3) to be
 * added to `coTeacherIds`. No separate temporary code is generated yet.
 *
 * Djay-zone: small bubble on empty co-teacher list (Zone 3).
 */
export function InviteCoTeacherPanel({
  open,
  onClose,
  classroom,
  activeAccount,
  teacherById,
  onRequestTransfer
}: InviteCoTeacherPanelProps) {
  const [copied, setCopied] = useState(false);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const coTeachers = classroom.coTeacherIds ?? [];
  const canAddMore = coTeachers.length < MAX_CO_TEACHERS_PER_CLASSROOM;
  const isOwner = classroom.teacherId === activeAccount.id;
  const handleCopy = () => {
    navigator.clipboard.writeText(classroom.joinCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <>
      <SlideOver open={open} onClose={onClose} title="Co-docent uitnodigen" description={`Voor klas: ${classroom.name}`} width="md" data-id="element-9363" data-mp="9rdxgt9mctz5">
        <div className="p-6 space-y-6" data-id="element-9364" data-mp="2cg41vdeo26pr">
          {/* Djay intro */}
          <div className="flex items-start gap-3 p-4 bg-indigo-50/60 border border-indigo-100 rounded-2xl" data-id="element-9365" data-mp="27g9c5d6mnn6a">
            <DjayNarrator size="xs" emotion="encouraging" data-id="element-9366" data-mp="22mn7o8amaplk" />
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9367" data-mp="1xpohu1xzl4t6">
              Werk je samen met een collega aan deze klas? Geef hen de klascode
              hieronder. Een co-docent kan leerlingen begeleiden en huiswerk
              uitzetten, maar alleen jij blijft eigenaar.
            </p>
          </div>

          {/* Invite code */}
          {isOwner && <div data-id="element-9368" data-mp="1jr6vo8nqc0dd">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9369" data-mp="1eqiwgzpckd82">
                Klascode delen
              </h3>
              {canAddMore ? <div className="p-4 bg-white border-2 border-dashed border-indigo-200 rounded-2xl" data-id="element-9370" data-mp="3e10tl7lt8uyy">
                  <div className="flex items-center justify-between gap-3" data-id="element-9371" data-mp="392h6v4ze7f5j">
                    <div data-id="element-9372" data-mp="3hl2bsxqkof8p">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-9373" data-mp="3lmw9cx5h134n">
                        Code
                      </p>
                      <p className="text-2xl font-mono font-black text-slate-900 tracking-widest" data-id="element-9374" data-mp="3qe5xftbdfyef">
                        {classroom.joinCode}
                      </p>
                    </div>
                    <Button onClick={handleCopy} variant="secondary" size="sm" className="flex items-center gap-2" data-id="element-9375" data-mp="2ynrma4uqt06g">
                      {copied ? <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-9376" data-mp="3cn994eoha3d6" />
                          Gekopieerd
                        </> : <>
                          <Copy className="w-4 h-4" data-id="element-9377" data-mp="ruxifptupfwg" />
                          Kopieer
                        </>}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-id="element-9378" data-mp="1vum2l4otozn7">
                    Je collega voert deze code in via{' '}
                    <span className="font-bold" data-id="element-9379" data-mp="29uwywn8wj6fr">
                      Klas Beheer → "Ik heb een co-docent-code"
                    </span>{' '}
                    in hun eigen docentportaal.
                  </p>
                </div> : <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl" data-id="element-9380" data-mp="2ethzins2fs4q">
                  <p className="text-sm font-bold text-amber-900" data-id="element-9381" data-mp="2skmql1hdbc3g">
                    Maximum bereikt
                  </p>
                  <p className="text-xs text-amber-800 mt-1" data-id="element-9382" data-mp="2ojkqkifmmw0f">
                    Deze klas heeft al {MAX_CO_TEACHERS_PER_CLASSROOM}{' '}
                    co-docenten. Verwijder eerst een co-docent om iemand anders
                    toe te voegen.
                  </p>
                </div>}
            </div>}

          {/* E-mail invite (placeholder for Wave 5) */}
          {isOwner && <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl" data-id="element-9383" data-mp="3m3b6r2csoxql">
              <div className="flex items-center gap-2 mb-1" data-id="element-9384" data-mp="3760mb3i6chiq">
                <Mail className="w-4 h-4 text-slate-400" data-id="element-9385" data-mp="3c5n1hihkc7u6" />
                <p className="text-sm font-bold text-slate-700" data-id="element-9386" data-mp="3c426jrrc6mvs">
                  Uitnodigen per e-mail
                </p>
                <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded" data-id="element-9387" data-mp="3358co95e4alx">
                  Binnenkort
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed" data-id="element-9388" data-mp="2tff2nckwc3js">
                E-mailuitnodigingen komen beschikbaar zodra het school-account
                aan een backend is gekoppeld.
              </p>
            </div>}

          {/* Current teachers */}
          <div data-id="element-9389" data-mp="2jjbwzwqu5ms3">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9390" data-mp="pyih81s5egcl">
              Huidige docenten
            </h3>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-9391" data-mp="l6qy2cbbncwh">
              {/* Owner */}
              <li className="flex items-center justify-between px-4 py-3" data-id="element-9392" data-mp="g6cpmt5t60xx">
                <div className="flex items-center gap-3 min-w-0" data-id="element-9393" data-mp="bb725pwrce7q">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-9394" data-mp="18xmnj2sb1w0k">
                    {(teacherById[classroom.teacherId]?.displayName ?? '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0" data-id="element-9395" data-mp="uwtqobej6oua">
                    <p className="text-sm font-bold text-slate-900 truncate" data-id="element-9396" data-mp="3jpfqumw5skum">
                      {teacherById[classroom.teacherId]?.displayName ?? 'Onbekend'}
                      {classroom.teacherId === activeAccount.id && <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-9397" data-mp="1v7cwsaxsscke">
                          (jij)
                        </span>}
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded" data-id="element-9398" data-mp="244n6vsyvpvnq">
                  Eigenaar
                </span>
              </li>

              {/* Co-teachers */}
              {coTeachers.length === 0 ? <li className="px-4 py-6 text-center" data-id="element-9399" data-mp="2itjgvvkhxiqz">
                  <div className="flex justify-center mb-2" data-id="element-9400" data-mp="2mwptrm24686f">
                    <Users className="w-5 h-5 text-slate-300" data-id="element-9401" data-mp="2mv4ujj4uu128" />
                  </div>
                  <p className="text-sm text-slate-500 font-medium" data-id="element-9402" data-mp="2rtyadi7m645k">
                    Nog geen co-docenten
                  </p>
                </li> : coTeachers.map(coId => {
              const co = teacherById[coId];
              const name = co?.displayName ?? 'Onbekende docent';
              return <li key={coId} className="flex items-center justify-between px-4 py-3" data-id="element-9403" data-mp="2vd6x53ivalch">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-9404" data-mp="346aa7aplow6l">
                        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-9405" data-mp="348d6gchcjljr">
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0" data-id="element-9406" data-mp="2qfevviwc1mvm">
                          <p className="text-sm font-bold text-slate-900 truncate" data-id="element-9407" data-mp="2qg6nyjqq12h5">
                            {name}
                            {coId === activeAccount.id && <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-9408" data-mp="5n38p40wzhjn">
                                (jij)
                              </span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-9409" data-mp="1nnccfh9ml14y">
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-700 rounded" data-id="element-9410" data-mp="19n1l6d4elkj9">
                          Co-docent
                        </span>
                        {isOwner && onRequestTransfer && <button onClick={() => setTransferTarget(coId)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Eigenaarschap overdragen aan deze co-docent" data-id="element-9411" data-mp="22da3q62uq6yd">
                            <ArrowRightLeft className="w-4 h-4" data-id="element-9412" data-mp="2l9rtihy53wrz" />
                          </button>}
                      </div>
                    </li>;
            })}
            </ul>
          </div>
        </div>
      </SlideOver>

      {transferTarget && onRequestTransfer && <ConfirmDialog open={true} title="Eigenaarschap overdragen?" message={`Hierna is ${teacherById[transferTarget]?.displayName ?? 'deze docent'} de nieuwe eigenaar van ${classroom.name}. Jij blijft betrokken als co-docent. Deze actie is niet ongedaan te maken.`} confirmLabel="Ja, draag over" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      onRequestTransfer(transferTarget);
      setTransferTarget(null);
      onClose();
    }} onCancel={() => setTransferTarget(null)} data-id="element-9413" data-mp="v0lyfa5tzfrx" />}
    </>;
}